import React, { useState, useEffect } from "react";
import { Alert, Dropdown, Spinner } from "react-bootstrap";
import { emojis } from "../../bootstrap/alertData";
import DeleteModal from "../../DeleteModal";
import {
  getSMServices,
  updateSMServices,
  DeleteSMServices,
  createSMServices,
} from "../../../../APIS";
const Services = () => {
  const [serviceData, setServiceData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [successMsg, setSuccessMsg] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [isEditMode, setIsEditMode] = useState(false);
  const [formData, setFormData] = useState(null);
  const [currentPage, setCurrentPage] = useState(0);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [serviceToDelete, setServiceToDelete] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const itemsPerPage = 15;

  // ✅ Fetch services
  const fetchServices = async () => {
    setLoading(true);
    try {
      const res = await getSMServices();
      setServiceData(res?.data?.services || []);
    } catch (err) {
      setError("Failed to fetch services.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchServices();
  }, []);

  // ✅ Toggle active status
  const handleToggleStatus = async (service) => {
    try {
      const updatedService = { ...service, is_active: !service.is_active };
      await updateSMServices(updatedService, service.id);
      setSuccessMsg(
        `Service "${service.service_name}" marked as ${
          updatedService.is_active ? "Active" : "Inactive"
        }.`
      );
      fetchServices();
    } catch (err) {
      setError("Failed to update service status.");
    } finally {
      setTimeout(() => {
        setError(null);
        setSuccessMsg(null);
      }, 3000);
    }
  };

  // ✅ Add new service
  const handleAdd = () => {
    setFormData({ service_name: "", is_active: true, cms: false });
    setIsEditMode(true);
  };

  // ✅ Edit existing service
  const handleEdit = (service) => {
    setFormData(service);
    setIsEditMode(true);
  };

  // ✅ Delete service - show confirmation modal
  const handleDelete = (service) => {
    setServiceToDelete(service);
    setShowDeleteModal(true);
  };

  // ✅ Confirm delete service
  const handleConfirmDelete = async () => {
    if (!serviceToDelete) return;

    setDeleteLoading(true);
    try {
      await DeleteSMServices(serviceToDelete.id);
      setSuccessMsg("Service deleted successfully.");
      fetchServices();
      setShowDeleteModal(false);
      setServiceToDelete(null);
    } catch (err) {
      setError("Failed to delete service.");
    } finally {
      setDeleteLoading(false);
      setTimeout(() => {
        setError(null);
        setSuccessMsg(null);
      }, 3000);
    }
  };

  // ✅ Form input change
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  // ✅ Submit form
  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (formData.id) {
        await updateSMServices(formData, formData.id);
        setSuccessMsg("Service updated successfully.");
      } else {
        await createSMServices(formData);
        setSuccessMsg("Service added successfully.");
      }
      setIsEditMode(false);
      fetchServices();
    } catch (err) {
      setError("Failed to save service.");
    } finally {
      setTimeout(() => {
        setError(null);
        setSuccessMsg(null);
      }, 3000);
    }
  };

  // ✅ Filtered + Paginated data
  const filteredServices = serviceData.filter((service) =>
    service.service_name?.toLowerCase().includes(searchTerm.toLowerCase())
  );
  const totalPage = Math.ceil(filteredServices.length / itemsPerPage);
  const paginatedContent = filteredServices.slice(
    currentPage * itemsPerPage,
    (currentPage + 1) * itemsPerPage
  );

  return (
    <div className="col-12">
      {successMsg && (
        <Alert
          variant="success"
          dismissible
          onClose={() => setSuccessMsg(null)}
        >
          {emojis.success} {successMsg}
        </Alert>
      )}
      {error && (
        <Alert variant="danger" dismissible onClose={() => setError(null)}>
          {emojis.warning} {error}
        </Alert>
      )}

      {isEditMode && formData ? (
        // ✅ Add/Edit Form
        <div className="card">
          <div className="card-header">
            <h4 className="card-title">
              {formData.id ? "Edit Service" : "Add Service"}
            </h4>
          </div>
          <div className="card-body">
            <form onSubmit={handleSubmit}>
              <div className="row">
                <div className="col-xl-6">
                  <div className="mb-3 row">
                    <label className="col-lg-4 col-form-label">
                      Service <span className="text-danger">*</span>
                    </label>
                    <div className="col-lg-6">
                      <input
                        type="text"
                        name="service_name"
                        value={formData.service_name || ""}
                        onChange={handleChange}
                        className="form-control"
                        placeholder="Enter service name..."
                        required
                      />
                    </div>
                  </div>
                </div>

                <div className="col-xl-6">
                  <div className="mb-3 row">
                    <label className="col-lg-4 col-form-label">Status</label>
                    <div className="col-lg-6">
                      <select
                        name="is_active"
                        value={formData.is_active}
                        onChange={(e) =>
                          setFormData((prev) => ({
                            ...prev,
                            is_active: e.target.value === "true",
                          }))
                        }
                        className="form-control"
                      >
                        <option value={true}>Active</option>
                        <option value={false}>Inactive</option>
                      </select>
                    </div>
                  </div>
                </div>

                <div className="col-xl-6">
                  <div className="mb-3 row">
                    <label className="col-lg-4 col-form-label">CMS</label>
                    <div className="col-lg-6">
                      <div className="form-check">
                        <input
                          type="checkbox"
                          name="cms"
                          checked={formData.cms || false}
                          onChange={(e) =>
                            setFormData((prev) => ({
                              ...prev,
                              cms: e.target.checked,
                            }))
                          }
                          className="form-check-input"
                        />
                        <label className="form-check-label">Enable CMS</label>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="mb-3 row">
                  <div className="col-lg-10 ms-auto">
                    <button type="submit" className="btn btn-secondary">
                      Save
                    </button>
                    <button
                      type="button"
                      className="btn btn-primary ms-2"
                      onClick={() => setIsEditMode(false)}
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              </div>
            </form>
          </div>
        </div>
      ) : (
        // ✅ Listing Services
        <div className="card">
          <div className="card-header d-flex justify-content-between align-items-center">
            <h4 className="card-title m-0">Services</h4>
            <div className="d-flex gap-2">
              <input
                type="text"
                className="form-control"
                placeholder="Search by service name"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
              <button
                className="btn btn-secondary"
                style={{ whiteSpace: "nowrap" }}
                onClick={handleAdd}
              >
                Add Service
              </button>
            </div>
          </div>

          <div className="card-body">
            <div className="table-responsive">
              {loading ? (
                <div className="d-flex justify-content-center align-items-center p-4">
                  <Spinner animation="border" role="status" />
                </div>
              ) : (
                <table className="table table-striped align-middle">
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>Service</th>
                      <th>CMS</th>
                      <th>Status</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {paginatedContent.length > 0 ? (
                      [...paginatedContent]
                        .sort((a, b) => a.id - b.id)
                        .map((service, i) => (
                          <tr key={service.id}>
                            <td>{service.id}</td>
                            <td>{service.service_name || "--"}</td>
                            <td>{service.cms ? "Enabled" : "Disabled"}</td>
                            <td>
                              <div className="form-check form-switch">
                                <input
                                  className="form-check-input"
                                  type="checkbox"
                                  checked={service.is_active}
                                  onChange={() => handleToggleStatus(service)}
                                />
                              </div>
                            </td>
                            <td>
                              <Dropdown>
                                <Dropdown.Toggle variant="link">
                                  &#x2022;&#x2022;&#x2022;
                                </Dropdown.Toggle>
                                <Dropdown.Menu align="end">
                                  <Dropdown.Item
                                    onClick={() => handleEdit(service)}
                                  >
                                    Edit
                                  </Dropdown.Item>
                                  <Dropdown.Item
                                    onClick={() => handleDelete(service)}
                                  >
                                    Delete
                                  </Dropdown.Item>
                                </Dropdown.Menu>
                              </Dropdown>
                            </td>
                          </tr>
                        ))
                    ) : (
                      <tr>
                        <td colSpan="5" className="text-center">
                          No services found.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              )}
            </div>
            {/* ✅ Pagination */}
            {!loading && filteredServices.length > 0 && (
              <div className="d-flex justify-content-between align-items-center mt-3">
                <div>
                  Showing{" "}
                  {filteredServices.length === 0
                    ? 0
                    : currentPage * itemsPerPage + 1}{" "}
                  to{" "}
                  {Math.min(
                    (currentPage + 1) * itemsPerPage,
                    filteredServices.length
                  )}{" "}
                  of {filteredServices.length} entries
                </div>
                <div>
                  <button
                    className={`btn btn-sm btn-outline-primary me-2 ${
                      currentPage === 0 ? "disabled" : ""
                    }`}
                    onClick={() =>
                      currentPage > 0 && setCurrentPage(currentPage - 1)
                    }
                  >
                    Prev
                  </button>
                  {Array.from({ length: totalPage }, (_, i) => (
                    <button
                      key={i}
                      className={`btn btn-sm border me-1 ${
                        currentPage === i ? "btn-primary text-white" : ""
                      }`}
                      onClick={() => setCurrentPage(i)}
                    >
                      {i + 1}
                    </button>
                  ))}
                  <button
                    className={`btn btn-sm btn-outline-primary ms-2 ${
                      currentPage === totalPage - 1 ? "disabled" : ""
                    }`}
                    onClick={() =>
                      currentPage + 1 < totalPage &&
                      setCurrentPage(currentPage + 1)
                    }
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      <DeleteModal
        show={showDeleteModal}
        onClose={() => {
          setShowDeleteModal(false);
          setServiceToDelete(null);
        }}
        onConfirm={handleConfirmDelete}
        title="Delete Service"
        message={`Are you sure you want to delete the service "${serviceToDelete?.service_name}"? This action cannot be undone.`}
        confirmText="Delete"
        isLoading={deleteLoading}
        danger={true}
      />
    </div>
  );
};
export default Services;
